/**
 * Base converter for React Flow node data to legacy HTML format
 * Handles common transformations shared across all node types
 */
import {
  convertNullToLegacyString,
  shouldSkipField,
} from '../../shared/fieldMappers'
import { NodeType } from '../../shared/reactFlowTypes'
import { convertKeyToSnakeCase } from '../utils'
import { booleanToLegacyNumber } from './utils'

function normalizeLegacyBooleanForComparison(
  value: unknown,
): boolean | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return value === 1
  }

  if (typeof value === 'string') {
    return value === '1' || value === 'true'
  }

  return Boolean(value)
}

function deriveLeaveVoicemailSoundClipIdFromLegacyRaw(
  rawMessageType: unknown,
  rawSoundClipId: unknown,
): string | undefined {
  const messageType =
    rawMessageType === null || rawMessageType === undefined
      ? undefined
      : String(rawMessageType)
  const soundClipId =
    rawSoundClipId === null || rawSoundClipId === undefined
      ? undefined
      : String(rawSoundClipId)

  if (messageType === '-1' && soundClipId && soundClipId !== 'null') {
    return soundClipId
  }
  if (messageType === '-2') {
    return 'system_generated'
  }
  if (messageType === '-3') {
    return 'no_greeting'
  }
  if (messageType === '0' && soundClipId === 'null') {
    return 'defined_by_extension'
  }
  return undefined
}

/**
 * Base converter that handles common transformations
 * @param nodeData - React Flow node data object
 * @param nodeType - React Flow node type
 * @param _nodeId - Optional node ID (currently unused in base converter)
 * @returns Legacy HTML format object
 */
export function baseConvertNodeDataToHtml(
  nodeData: Record<string, unknown>,
  nodeType: NodeType,
  _nodeId?: string,
): Record<string, unknown> {
  const html: Record<string, unknown> = {}

  Object.entries(nodeData).forEach(([key, value]) => {
    // Skip label as it's not part of html
    if (key === 'label') {
      return
    }

    // Skip Admin3-only metadata fields and connection metadata fields
    if (shouldSkipField(key)) {
      return
    }

    // Leave voicemail nodes loaded from legacy can carry both raw legacy fields
    // (message_type / sound_clip_id) and a derived soundClipId value for UI.
    // If the derived value simply mirrors the raw fields, keep raw fields as source
    // of truth to avoid changing JSON null/missing representation on save.
    if (nodeType === NodeType.LeaveVoicemail && key === 'soundClipId') {
      const hasRawMessageType = Object.prototype.hasOwnProperty.call(
        nodeData,
        'message_type',
      )
      const hasRawSoundClipId = Object.prototype.hasOwnProperty.call(
        nodeData,
        'sound_clip_id',
      )
      if (hasRawMessageType || hasRawSoundClipId) {
        const derived = deriveLeaveVoicemailSoundClipIdFromLegacyRaw(
          nodeData['message_type'],
          nodeData['sound_clip_id'],
        )
        const current =
          value === null || value === undefined || value === ''
            ? undefined
            : String(value)
        if (current === derived) {
          return
        }
      }

      // Keep React Flow's soundClipId key until ensureRequiredHtmlFields() runs.
      // If we immediately snake-case it to sound_clip_id, that function cannot
      // distinguish React Flow intent from raw legacy fields.
      html['soundClipId'] = value
      return
    }

    // Special handling: SMS uses sms_number instead of sendFrom
    // NOTE: For SMS nodes, sendFrom conversion is handled in convertSmsNotificationNodeData
    // which converts messaging group IDs to phone numbers. We skip it here to let the
    // node-specific converter handle it properly.
    if (nodeType === NodeType.SmsNotification && key === 'sendFrom') {
      // Skip this field - it will be handled by convertSmsNotificationNodeData
      return
    }

    // Special handling: If send_from already exists (from previous conversion), skip it
    // (node-specific converter will handle conversion to sms_number)
    if (nodeType === NodeType.SmsNotification && key === 'send_from') {
      // Skip this field - it will be handled by convertSmsNotificationNodeData
      return
    }

    // Special handling: ExternalTransfer uses 'number' instead of 'phone_number'
    // NOTE: For ExternalTransfer nodes, phoneNumber conversion is handled in convertExternalTransferNodeData
    // which converts phoneNumber to 'number' field. We skip it here to let the
    // node-specific converter handle it properly.
    if (nodeType === NodeType.ExternalTransfer && key === 'phoneNumber') {
      // Skip this field - it will be handled by convertExternalTransferNodeData
      return
    }

    // Special handling: Webhook uses notify_url instead of url
    // This must happen BEFORE convertKeyToSnakeCase to prevent url → url
    if (nodeType === NodeType.Webhook && key === 'url') {
      html['notify_url'] =
        value === null || value === undefined || value === ''
          ? null
          : String(value)
      return // Skip normal processing for this field
    }

    const snakeKey = convertKeyToSnakeCase(key, nodeType)

    // CRITICAL: Preserve line_id for direct_line (Transfer node type)
    // Handle both extensionId and lineId field names to ensure value is preserved
    if (
      nodeType === NodeType.Transfer &&
      (key === 'extensionId' || key === 'lineId')
    ) {
      // Ensure value is preserved - convert to string if not null/undefined/empty
      if (value !== null && value !== undefined && value !== '') {
        html['line_id'] = String(value)
      } else {
        html['line_id'] = null
      }
      return // Skip normal processing for this field
    }

    // CRITICAL: Preserve extension_id for simple_dial (TransferAndContinue node type)
    // Ensure extensionId field is always checked and preserved
    if (
      nodeType === NodeType.TransferAndContinue &&
      (key === 'extensionId' || key === 'extension_id')
    ) {
      // Ensure value is preserved - convert to string if not null/undefined/empty
      if (value !== null && value !== undefined && value !== '') {
        html['extension_id'] = String(value)
      } else {
        html['extension_id'] = null
      }
      return // Skip normal processing for this field
    }

    // P1-1 Fix: Special handling for boolean fields that should remain as booleans (not converted)
    // These fields match Legacy Admin which saves booleans: allow_extension_dialing, pass_through_caller_id, ring_during_wait
    // Legacy Admin code saves these as JSON booleans (true/false), not strings or numbers
    // Reference: Legacy Admin code (edit_switchboard.php lines 7745, 7802, 7849)
    if (
      (nodeType === NodeType.AutoAttendant &&
        key === 'allowExtensionDialing') ||
      (nodeType === NodeType.ExternalTransfer &&
        key === 'passThroughCallerId') ||
      (nodeType === NodeType.Wait && key === 'ringDuringWait')
    ) {
      // Preserve raw legacy snake_case values when both variants exist (legacy -> ReactFlow -> legacy).
      // This avoids overwriting intentionally odd legacy payloads such as "0"/"1" strings,
      // unless the camelCase value has diverged (meaning the user edited it in Admin2).
      if (
        key !== snakeKey &&
        Object.prototype.hasOwnProperty.call(nodeData, snakeKey)
      ) {
        const normalizedCamelValue = normalizeLegacyBooleanForComparison(value)
        const normalizedSnakeValue = normalizeLegacyBooleanForComparison(
          nodeData[snakeKey],
        )

        if (
          normalizedCamelValue === undefined ||
          normalizedCamelValue === normalizedSnakeValue
        ) {
          return
        }
      }

      // Handle boolean input - keep as boolean (matching Legacy Admin behavior)
      if (typeof value === 'boolean') {
        html[snakeKey] = value
        return
      }
      // Handle string "0"/"1" or "true"/"false" input - convert to boolean
      // This handles cases where data comes in as strings from previous conversions
      if (typeof value === 'string') {
        html[snakeKey] = value === '1' || value === 'true'
        return
      }
      // For any other type, convert to boolean
      html[snakeKey] = Boolean(value)
      return
    }

    // Handle other boolean fields that should be converted to numbers
    if (typeof value === 'boolean') {
      if (
        key === 'autoReplyToCaller' ||
        key === 'minimized' ||
        key === 'press1'
      ) {
        // Legacy format uses numbers 0/1 for these boolean fields
        html[snakeKey] = booleanToLegacyNumber(value)
        return
      }
    }

    if (
      typeof value === 'number' &&
      (key === 'timeout' ||
        key === 'duration' ||
        key === 'repeatTimes' ||
        key.includes('Id'))
    ) {
      html[snakeKey] = String(value)
      return
    }

    // Special handling: maxWaitTime should be converted to string to match legacy format
    // Legacy format stores max_wait_time as a string (e.g., '60')
    if (nodeType === NodeType.Queue && key === 'maxWaitTime') {
      html[snakeKey] = String(value)
      return
    }

    // CRITICAL: Preserve schedule_id for Schedule node type
    // Ensure scheduleId field is always checked and preserved
    if (
      nodeType === NodeType.Schedule &&
      (key === 'scheduleId' || snakeKey === 'schedule_id')
    ) {
      if (value !== null && value !== undefined && value !== '') {
        html['schedule_id'] = String(value)
      } else {
        html['schedule_id'] = null
      }
      return // Skip normal processing for this field
    }

    // CRITICAL: Preserve fax_extension_id for FaxDetect node type
    // Ensure faxExtensionId field is always checked and preserved
    if (
      nodeType === NodeType.FaxDetect &&
      (key === 'faxExtensionId' || snakeKey === 'fax_extension_id')
    ) {
      if (value !== null && value !== undefined && value !== '') {
        html['fax_extension_id'] = String(value)
      } else {
        html['fax_extension_id'] = null
      }
      return // Skip normal processing for this field
    }

    // P1-4 Fix: CRITICAL: Preserve lookup_id for dial_name (DialByName node type)
    // Ensure lookupId field is always checked and preserved correctly
    // Legacy saves JSON null when no radio button is checked, NOT string "undefined"
    if (
      nodeType === NodeType.DialByName &&
      (key === 'lookupId' || snakeKey === 'lookup_id')
    ) {
      // Handle undefined/null/empty values correctly
      if (value === null || value === undefined || value === '') {
        // If no value, set to null (JSON null, NOT string "undefined")
        // Legacy saves null when no radio button is checked (line 7652)
        html['lookup_id'] = null
      } else {
        // Ensure value is a valid string
        const lookupIdValue = String(value)
        // Validate it's a valid lookup_id value
        if (
          lookupIdValue === 'extension_name' ||
          lookupIdValue === 'username'
        ) {
          html['lookup_id'] = lookupIdValue
        } else if (lookupIdValue === 'undefined' || lookupIdValue === 'null') {
          // If somehow we got the string "undefined" or "null", set to null
          // This handles edge cases where the value might have been incorrectly converted
          html['lookup_id'] = null
        } else {
          // Unknown value - default to extension_name (matching legacy default)
          html['lookup_id'] = 'extension_name'
        }
      }
      return // Skip normal processing (including convertNullToLegacyString)
    }

    // Special handling: agent_id (ai_agent_v1) - empty strings become null
    // Legacy: saves null if empty (line 7817: checks val != '')
    if (
      nodeType === NodeType.AiAgentV1 &&
      (key === 'agentId' || snakeKey === 'agent_id')
    ) {
      if (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '')
      ) {
        html[snakeKey] = null
      } else {
        html[snakeKey] = String(value)
      }
      return
    }

    // Convert null/undefined to legacy string format if needed
    const convertedValue = convertNullToLegacyString(value, snakeKey)

    html[snakeKey] = convertedValue
  })

  return html
}
